/**
 * RowAction — an index table's row action, as an icon with its name on hover (design rule 13).
 *
 * The owner's sentence: *"wherever you are using our action, replace the action with the
 * action icons, and when we hover over it use tooltip to show the action title or name,
 * because right now it's taking too much space everywhere — this is for the index tables."*
 *
 * WHY A PRIMITIVE AND NOT THIRTEEN COPIES. The rule has four halves — icon-only `.btn--sm
 * .btn--icon`, `aria-label` = the former label, the portaled `Tooltip` on hover AND focus, and
 * **no visible text node** — and thirteen call sites writing them out by hand is thirteen
 * chances for one of them to drop the `aria-label` and ship a button a screen reader reads as
 * "button". `options/ChoiceRow.tsx` had already done exactly that before this wave: two
 * icon-only buttons, no accessible name at all, because nothing named the shape.
 *
 * THE NAME AND THE DESCRIPTION ARE TWO DIFFERENT STRINGS (F-5). `label` is the name and the
 * default tooltip; `hint` replaces the TOOLTIP only, for the actions whose verb cannot carry
 * what they do — Remove and Delete are adjacent in one cell on the Templates index, and the
 * sentence is the entire difference between "this device only" and "the whole account".
 *
 * WHY THE NAME IS `aria-label` AND NOT THE TOOLTIP TEXT. A `role="tooltip"` element is a
 * DESCRIPTION, not a name: `aria-describedby` is announced after the name and after a pause,
 * and is suppressed entirely in some verbosity settings. The name has to be on the control.
 * `Tooltip` therefore carries the same string twice on purpose — `aria-label` for the name and
 * the bubble for the sighted user who no longer has the word in the row.
 *
 * WHY `Tooltip` AND NOT `title`. `title` is the platform's tooltip: a ~1s delay, no styling,
 * and on a touch device nothing at all. `ui/Tooltip.tsx` shows on focus as well as hover and
 * portals its bubble onto `document.body`, which is the only reason it is legible at all
 * inside `.table-scroll` — an `overflow: auto` box clips both axes.
 *
 * TIERS. `tone` maps to the button vocabulary, and nothing else: `"quiet"` is `.btn--ghost`,
 * `"danger"` is `.btn--danger`, and the default is the bare `.btn` tier. There is deliberately
 * no primary tier — the One Voice Rule puts a page's one primary in its `.actionbar`, never in
 * a row that repeats twenty-four times.
 *
 * HIT TARGET. `.btn--sm.btn--icon` draws 30×30 and carries the shared transparent 32px ring
 * from `components.css` (G-26), so shrinking the box does not shrink the target.
 */
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Icon, type IconName } from "./Icon.js";
import { Tooltip } from "./Tooltip.js";

/** The three tiers a row action may take. A row never carries the page's primary. */
export type RowActionTone = "default" | "quiet" | "danger";

export type RowActionProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "aria-label" | "title" | "aria-busy"
> & {
  /** The action's name — its accessible name, and the tooltip when there is no `hint`. */
  label: string;
  /**
   * The longer sentence the button used to carry in its `title`, shown in the tooltip INSTEAD
   * of the label (F-5). The name stays `label`: a screen reader announces the name on every
   * pass over the row, and a sentence there is unusable — that is what a DESCRIPTION is for.
   *
   * Use it wherever the verb alone cannot tell two adjacent actions apart. "Remove" and
   * "Delete" share one cell on the Templates index, and "Remove from this device only (stays
   * in the cloud)" against "Delete from the cloud (whole account)" is the whole difference.
   */
  hint?: string;
  /** The verb's icon. One icon per verb, product-wide; the map is in `docs/design.md` rule 13. */
  icon: IconName;
  /** `.btn--ghost` for quiet verbs, `.btn--danger` for destructive ones. */
  tone?: RowActionTone;
  /** Where the bubble opens. `"top"` for the last rows of a long table. */
  placement?: "bottom" | "top";
  /**
   * The request this action fired is in flight (F-7). The icon is replaced by the shared
   * `.btn-spinner` and `aria-busy` goes on.
   *
   * It is a PROP and not the caller's job because the four call sites that needed it were all
   * doing the same thing before this wave — printing "…" instead of their label — and reduced
   * to an icon each of them silently showed nothing. `disabled` alone is not feedback: a
   * button that is briefly grey and a button that is grey because the row is working look the
   * same, and `aria-busy` is visible to nobody.
   */
  busy?: boolean;
};

const TONE_CLASS: Record<RowActionTone, string> = {
  default: "",
  quiet: " btn--ghost",
  danger: " btn--danger",
};

export const RowAction = forwardRef<HTMLButtonElement, RowActionProps>(function RowAction(
  { label, hint, icon, tone = "default", placement = "bottom", busy = false, className, type, ...rest },
  ref,
) {
  return (
    <Tooltip text={hint ?? label} placement={placement}>
      <button
        {...rest}
        ref={ref}
        type={type ?? "button"}
        className={`btn btn--sm btn--icon${TONE_CLASS[tone]}${className ? ` ${className}` : ""}`}
        aria-label={label}
        aria-busy={busy || undefined}
      >
        {/* One or the other, never both: an icon AND a spinner do not fit a 30x30 box. */}
        {busy ? <span className="btn-spinner" aria-hidden="true" /> : <Icon name={icon} size={16} />}
      </button>
    </Tooltip>
  );
});
